import React, { useState } from 'react';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What does a doula do?',
      answer: (
        <div className="space-y-4">
          <p>A doula is someone who supports women as she prepares for birth and steps into motherhood.</p>
          <p>My role is to offer emotional support, practical guidance and a calm space to ask questions, understand your options and make informed decisions.</p>
          <p>I don't replace your midwife or make decisions for you. I'm there to walk alongside you, so you feel supported in a way that honours your own values, choices and experiences.</p>
        </div>
      ),
    },
    {
      question: "What's the difference between a doula and a midwife?",
      answer: (
        <div className="space-y-4">
          <p>Both play important roles, but they support you in different ways.</p>
          <p>Your midwife is responsible for the medical care of you and your baby. She monitors your health, carries out clinical assessments, and helps keep your pregnancy, birth and postpartum period safe.</p>
          <p>My role is different. I offer emotional support, practical guidance and evidence-informed information, creating a space where you can ask questions, understand your options and make decisions that feel right for you.</p>
          <p>Rather than replacing your healthcare team, I work alongside them so you feel supported not only physically, but emotionally too.</p>
          <p>Your midwife cares for your clinical wellbeing. I help hold space for the woman experiencing it all.</p>
        </div>
      ),
    },
    {
      question: 'Do you support birth itself?',
      answer: (
        <div className="space-y-4">
          <p>My Birth Preparation service is designed to help you approach birth feeling informed, calm and guided by your own instincts.</p>
          <p>At the moment, I don't offer on-call labour support or attend births in person. Instead, I focus on helping women prepare thoughtfully before birth and supporting them through the early weeks of motherhood afterwards.</p>
          <p>If having a doula by your side during labour is important to you, I'd be delighted to recommend trusted colleagues whose approach and values align closely with Soul Bridge.</p>
          <p>Whether we work together before birth, after birth, or both, my hope is that you'll feel cared for, understood and able to find your own way through this season of motherhood.</p>
        </div>
      ),
    },
    {
      question: 'When should I book?',
      answer: (
        <div className="space-y-4">
          <p>If you're interested in Birth Preparation or Postpartum Planning, many women choose to get in touch during the second trimester so we have plenty of time to work together.</p>
          <p>If you're looking for Postpartum Support, it's often helpful to arrange this before your baby arrives, although it's never too late to reach out.</p>
          <p>There isn't a perfect time to reach out. We'll begin with where you are today.</p>
        </div>
      ),
    },
    {
      question: 'What happens during a Connection Call?',
      answer: (
        <div className="space-y-4">
          <p>Think of it as a conversation rather than a consultation.</p>
          <p>It's a gentle space for us to get to know each other. You'll have the opportunity to share where you are in your journey, ask any questions you may have, and explore the kind of support you're looking for.</p>
          <p>I'll listen, answer your questions, and explain how I work, so together we can explore what feels right for you and your family.</p>
          <p>Whether we decide to work together or not, my hope is that you'll leave the conversation feeling heard, supported and a little clearer about your next steps.</p>
        </div>
      ),
    },
    {
      question: 'Can my partner be involved?',
      answer: (
        <div className="space-y-4">
          <p>Absolutely.</p>
          <p>Whether we're preparing for birth or planning for the postpartum period, partners are warmly welcome to be involved.</p>
          <p>I'll help you both feel informed and prepared, creating space for open conversations, shared understanding and practical ways to support one another.</p>
          <p>Supporting you as a team helps build a stronger foundation for the transition into parenthood.</p>
        </div>
      ),
    },
    {
      question: 'Can I book only postpartum support?',
      answer: (
        <div className="space-y-4">
          <p>Yes.</p>
          <p>In fact, many women come to Soul Bridge specifically for postpartum support.</p>
          <p>While so much attention is given to preparing for birth, the weeks and months afterwards deserve just as much care. This is a season of healing, adjustment and finding your own rhythm as a mother, and you don't have to navigate it alone.</p>
          <p>Whether your baby is a few days, a few weeks or a few months old, we'll begin with where you are and create the support that feels right for you.</p>
        </div>
      ),
    },
    {
      question: 'Do you offer online sessions?',
      answer: (
        <div className="space-y-4">
          <p>Yes.</p>
          <p>Some forms of support naturally work beautifully online. Birth Preparation and Postpartum Planning can both be offered virtually, giving you the same space to reflect, ask questions and prepare with intention, wherever you are.</p>
          <p>For families based locally, Postpartum Support is offered in person so I can provide hands-on support during the early weeks of motherhood.</p>
          <p>If you're not sure which option is the best fit, we'll explore it together during our Connection Call.</p>
        </div>
      ),
    },
    {
      question: 'What areas do you cover?',
      answer: (
        <div className="space-y-4">
          <p>I'm based in Surrey and offer in-person Postpartum Support to families across the local area.</p>
          <p>Birth Preparation and Postpartum Planning are also available online, so we can work together wherever you're based.</p>
          <p>If you're unsure whether you're within my travel area, please get in touch. I'd be happy to let you know.</p>
        </div>
      ),
    },
    {
      question: 'Do you support all kinds of families?',
      answer: (
        <div className="space-y-4">
          <p>Absolutely.</p>
          <p>Soul Bridge is a welcoming, inclusive and judgement-free space where every family is treated with kindness, respect and compassion.</p>
          <p>Whoever you are, whatever your family looks like, you'll be met with openness, care and support that honours your individual journey.</p>
        </div>
      ),
    },
    {
      question: 'How does investment work?',
      answer: (
        <div className="space-y-4">
          <p>There's no one-size-fits-all approach to support, so there isn't one-size-fits-all investment either.</p>
          <p>Some families book a few visits during the early weeks after birth, while others choose ongoing support through one of my postpartum packages.</p>
          <p>We'll begin by talking about where you are in your journey and the kind of support you're looking for. From there, I'll recommend the option that feels most appropriate.</p>
          <p>You can find full pricing on the Support page, and we'll go through any questions together during your Connection Call.</p>
        </div>
      ),
    },
    {
      question: 'Can you help with breastfeeding?',
      answer: (
        <div className="space-y-4">
          <p>Yes.</p>
          <p>Feeding your baby can be a beautiful experience, but it can also bring questions, uncertainty and unexpected challenges. You don't have to navigate that alone.</p>
          <p>I'll offer gentle guidance, practical support and reassurance, helping you find what feels right for you and your baby. If more specialist support is needed, I'm happy to recommend trusted lactation consultants while continuing to support you throughout your journey.</p>
        </div>
      ),
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#f5f4f0]">
      {/* Hero Section */}
      <section className="pt-[3.06rem] pb-8 px-4 bg-[#f5f4f0]">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-5xl md:text-6xl mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: '#815863' }}
          >
            Frequently Asked Questions
          </h1>
          <p className="text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: '#4d6654' }}>
            It's natural to have questions when you're considering support. Here are some of the things women often ask before we first connect.
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="pt-16 pb-20 px-4 bg-[#f5f4f0]">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden transition-all duration-300"
                style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.06)' }}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left p-6 flex justify-between items-center"
                >
                  <h3 className="text-lg font-semibold pr-8" style={{ color: '#815863' }}>{faq.question}</h3>
                  <span
                    className={`text-2xl text-[#815863] transition-transform duration-300 ${
                      openIndex === index ? 'rotate-45' : ''
                    }`}
                  >
                    +
                  </span>
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Two columns with text left, image right */}
      <section className="py-10 md:py-20 px-4 bg-[#f5f4f0]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="text-left">
              <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#815863' }}>
                Ready to explore what support could look like for you?
              </h2>
              <p className="text-xl mb-8 leading-relaxed" style={{ color: '#4d6654' }}>
                Whether you're preparing for birth, thinking ahead to postpartum, or simply wondering where to begin, I'd love to hear where you are right now.
              </p>
              <a
                href="/contact"
                className="inline-block bg-[#815863] text-white px-8 py-3 rounded-full tracking-wider text-sm hover:bg-[#4d6654] transition-all duration-300 hover:scale-105"
              >
                BOOK A CONNECTION CALL
              </a>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ded95140-8b50-4155-82a1-50fd9b34f147/artifacts/6hjmy4cm_Wonder_flowers3.webp"
                alt="Gentle support"
                className="w-full h-[500px] object-cover rounded-3xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <div className="py-8 px-4 bg-[#f5f4f0] text-center">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center gap-2 text-[#815863] hover:text-[#4d6654] transition-colors duration-300"
          style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          Back to Top
        </button>
      </div>
    </div>
  );
};

export default FAQs;